// src/state/projectStore.ts

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { openDB, DBSchema, IDBPDatabase } from 'idb';
import type {
  ProjectState,
  SourceFile,
  Placement,
  BOMLine,
  PartGroup,
  ProjectAction,
  BoardData,
  AssemblyProgress,
  UIState,
  ProjectConfig
} from '../types';

// ============================================================================
// DATABASE SETUP
// ============================================================================

interface PCBAssemblyDB extends DBSchema {
  projects: {
    key: string;
    value: ProjectState;
    indexes: { 'by-updated': string };
  };
  files: {
    key: string;
    value: {
      id: string;
      projectId: string;
      name: string;
      content: Blob;
    };
    indexes: { 'by-project': string };
  };
}

let dbPromise: Promise<IDBPDatabase<PCBAssemblyDB>> | null = null;

export function getDB(): Promise<IDBPDatabase<PCBAssemblyDB>> {
  if (!dbPromise) {
    dbPromise = openDB<PCBAssemblyDB>('pcb-assembly', 1, {
      upgrade(db) {
        // Projects store
        const projectsStore = db.createObjectStore('projects', { keyPath: 'id' });
        projectsStore.createIndex('by-updated', 'updatedAt');
        
        // Files store
        const filesStore = db.createObjectStore('files', { keyPath: 'id' });
        filesStore.createIndex('by-project', 'projectId');
      }
    });
  }
  return dbPromise;
}

// ============================================================================
// INITIAL STATE
// ============================================================================

const initialUIState: UIState = {
  currentView: 'top',
  zoom: 1,
  pan: { x: 0, y: 0 },
  showLayers: {
    copper: true,
    soldermask: true,
    silkscreen: true,
    paste: false,
    drills: true,
    outline: true,
    placements: true
  },
  selectedPlacementId: null,
  hoveredPlacementId: null,
  showCompleted: true,
  showSkipped: true
};

const initialProgress: AssemblyProgress = {
  currentGroupId: null,
  currentPlacementId: null,
  completedRefDes: [],
  skippedRefDes: [],
  startTime: new Date().toISOString(),
  lastResumeTime: new Date().toISOString(),
  totalTimeSeconds: 0
};

const initialConfig: ProjectConfig = {
  pnpMapping: null,
  bomMapping: null,
  coordinateTransform: {
    offsetX: 0,
    offsetY: 0,
    scale: 1,
    rotation: 0,
    mirrorX: false,
    mirrorY: false
  },
  rotationOffset: 0,
  flipBottomRotation: false
};

export function createInitialState(name: string): ProjectState {
  return {
    id: `proj_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`,
    name,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    version: 1,
    files: [],
    board: null,
    placements: [],
    bomLines: [],
    groups: [],
    groupSort: 'category-priority',
    progress: initialProgress,
    ui: initialUIState,
    config: initialConfig
  };
}

// ============================================================================
// STORE ACTIONS
// ============================================================================

export function createStoreActions(set: any, get: any) {
  return {
    // File operations
    importFiles: (files: SourceFile[]) => {
      set((state: ProjectState) => ({
        ...state,
        files: [...state.files, ...files],
        updatedAt: new Date().toISOString()
      }));
    },
    
    removeFile: (fileId: string) => {
      set((state: ProjectState) => ({
        ...state,
        files: state.files.filter(f => f.id !== fileId),
        updatedAt: new Date().toISOString()
      }));
    },
    
    // Data operations
    setBoard: (board: BoardData | null) => {
      set((state: ProjectState) => ({
        ...state,
        board,
        updatedAt: new Date().toISOString()
      }));
    },
    
    setPlacements: (placements: Placement[]) => {
      set((state: ProjectState) => ({
        ...state,
        placements,
        updatedAt: new Date().toISOString()
      }));
    },
    
    setBOMLines: (bomLines: BOMLine[]) => {
      set((state: ProjectState) => ({
        ...state,
        bomLines,
        updatedAt: new Date().toISOString()
      }));
    },
    
    setGroups: (groups: PartGroup[]) => {
      set((state: ProjectState) => ({
        ...state,
        groups,
        updatedAt: new Date().toISOString()
      }));
    },
    
    // Progress operations
    markPlaced: (placementId: string) => {
      set((state: ProjectState) => {
        const updatedPlacements = state.placements.map(p =>
          p.id === placementId
            ? { ...p, status: 'placed' as const, placedAt: new Date().toISOString() }
            : p
        );
        
        return {
          ...state,
          placements: updatedPlacements,
          progress: {
            ...state.progress,
            completedRefDes: [...state.progress.completedRefDes, placementId],
            currentPlacementId: null
          },
          updatedAt: new Date().toISOString()
        };
      });
    },
    
    markUnplaced: (placementId: string) => {
      set((state: ProjectState) => {
        const updatedPlacements = state.placements.map(p =>
          p.id === placementId
            ? { ...p, status: 'pending' as const, placedAt: undefined }
            : p
        );
        
        return {
          ...state,
          placements: updatedPlacements,
          progress: {
            ...state.progress,
            completedRefDes: state.progress.completedRefDes.filter(id => id !== placementId)
          },
          updatedAt: new Date().toISOString()
        };
      });
    },
    
    skipPlacement: (placementId: string) => {
      set((state: ProjectState) => {
        const updatedPlacements = state.placements.map(p =>
          p.id === placementId
            ? { ...p, status: 'skipped' as const }
            : p
        );
        
        return {
          ...state,
          placements: updatedPlacements,
          progress: {
            ...state.progress,
            skippedRefDes: [...state.progress.skippedRefDes, placementId]
          },
          updatedAt: new Date().toISOString()
        };
      });
    },
    
    unskipPlacement: (placementId: string) => {
      set((state: ProjectState) => {
        const updatedPlacements = state.placements.map(p =>
          p.id === placementId
            ? { ...p, status: 'pending' as const }
            : p
        );
        
        return {
          ...state,
          placements: updatedPlacements,
          progress: {
            ...state.progress,
            skippedRefDes: state.progress.skippedRefDes.filter(id => id !== placementId)
          },
          updatedAt: new Date().toISOString()
        };
      });
    },
    
    // Navigation
    selectPlacement: (placementId: string | null) => {
      set((state: ProjectState) => ({
        ...state,
        ui: {
          ...state.ui,
          selectedPlacementId: placementId
        },
        updatedAt: new Date().toISOString()
      }));
    },
    
    hoverPlacement: (placementId: string | null) => {
      set((state: ProjectState) => ({
        ...state,
        ui: {
          ...state.ui,
          hoveredPlacementId: placementId
        }
      }));
    },
    
    nextPlacement: () => {
      // TODO: Implement navigation logic
      const state = get();
      // Find next unplaced placement in current group or next group
    },
    
    prevPlacement: () => {
      // TODO: Implement navigation logic
    },
    
    jumpToGroup: (groupId: string) => {
      set((state: ProjectState) => ({
        ...state,
        progress: {
          ...state.progress,
          currentGroupId: groupId
        },
        updatedAt: new Date().toISOString()
      }));
    },
    
    // View operations
    setView: (side: 'top' | 'bottom') => {
      set((state: ProjectState) => ({
        ...state,
        ui: {
          ...state.ui,
          currentView: side
        },
        updatedAt: new Date().toISOString()
      }));
    },
    
    setZoom: (zoom: number) => {
      set((state: ProjectState) => ({
        ...state,
        ui: {
          ...state.ui,
          zoom: Math.max(0.1, Math.min(10, zoom))
        },
        updatedAt: new Date().toISOString()
      }));
    },
    
    setPan: (pan: { x: number; y: number }) => {
      set((state: ProjectState) => ({
        ...state,
        ui: {
          ...state.ui,
          pan
        },
        updatedAt: new Date().toISOString()
      }));
    },
    
    toggleLayer: (layer: keyof UIState['showLayers']) => {
      set((state: ProjectState) => ({
        ...state,
        ui: {
          ...state.ui,
          showLayers: {
            ...state.ui.showLayers,
            [layer]: !state.ui.showLayers[layer]
          }
        },
        updatedAt: new Date().toISOString()
      }));
    },
    
    // Configuration
    setPnPMapping: (mapping: ProjectConfig['pnpMapping']) => {
      set((state: ProjectState) => ({
        ...state,
        config: {
          ...state.config,
          pnpMapping: mapping
        },
        updatedAt: new Date().toISOString()
      }));
    },
    
    setBOMMapping: (mapping: ProjectConfig['bomMapping']) => {
      set((state: ProjectState) => ({
        ...state,
        config: {
          ...state.config,
          bomMapping: mapping
        },
        updatedAt: new Date().toISOString()
      }));
    },
    
    setCoordinateTransform: (transform: ProjectConfig['coordinateTransform']) => {
      set((state: ProjectState) => ({
        ...state,
        config: {
          ...state.config,
          coordinateTransform: transform
        },
        updatedAt: new Date().toISOString()
      }));
    },
    
    // Import/Export
    exportProject: () => {
      const state = get();
      return JSON.stringify({
        version: state.version,
        name: state.name,
        createdAt: state.createdAt,
        updatedAt: state.updatedAt,
        placements: state.placements,
        bomLines: state.bomLines,
        groups: state.groups,
        progress: state.progress,
        config: state.config
      }, null, 2);
    },
    
    importProject: (json: string) => {
      try {
        const data = JSON.parse(json);
        
        // Validate version
        if (!data.version || data.version < 1) {
          throw new Error('Invalid project file version');
        }
        
        set((state: ProjectState) => ({
          ...state,
          id: `proj_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`,
          name: data.name || 'Imported Project',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          version: data.version,
          placements: data.placements || [],
          bomLines: data.bomLines || [],
          groups: data.groups || [],
          progress: data.progress || initialProgress,
          config: data.config || initialConfig
        }));
        
        return { success: true };
      } catch (error) {
        return { 
          success: false, 
          error: error instanceof Error ? error.message : 'Import failed' 
        };
      }
    },
    
    // Reset
    resetProgress: () => {
      set((state: ProjectState) => ({
        ...state,
        placements: state.placements.map(p => ({
          ...p,
          status: 'pending' as const,
          placedAt: undefined
        })),
        progress: {
          ...initialProgress,
          startTime: new Date().toISOString(),
          lastResumeTime: new Date().toISOString()
        },
        updatedAt: new Date().toISOString()
      }));
    }
  };
}

// ============================================================================
// ZUSTAND STORE
// ============================================================================

export const useProjectStore = create<
  ProjectState & ReturnType<typeof createStoreActions>
>()(
  persist(
    (set, get) => ({
      ...createInitialState('New Project'),
      ...createStoreActions(set, get)
    }),
    {
      name: 'pcb-assembly-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        id: state.id,
        name: state.name,
        createdAt: state.createdAt,
        updatedAt: state.updatedAt,
        version: state.version,
        placements: state.placements,
        bomLines: state.bomLines,
        groups: state.groups,
        groupSort: state.groupSort,
        progress: state.progress,
        ui: state.ui,
        config: state.config
      })
    }
  )
);

// ============================================================================
// DATABASE OPERATIONS
// ============================================================================

export async function saveProjectToDB(project: ProjectState): Promise<void> {
  const db = await getDB();
  await db.put('projects', project);
}

export async function loadProjectFromDB(projectId: string): Promise<ProjectState | undefined> {
  const db = await getDB();
  return db.get('projects', projectId);
}

export async function deleteProjectFromDB(projectId: string): Promise<void> {
  const db = await getDB();
  await db.delete('projects', projectId);
  
  // Also delete associated files
  const files = await db.getAllFromIndex('files', 'by-project', projectId);
  for (const file of files) {
    await db.delete('files', file.id);
  }
}

export async function listProjectsFromDB(): Promise<Pick<ProjectState, 'id' | 'name' | 'createdAt' | 'updatedAt'>[]> {
  const db = await getDB();
  const projects = await db.getAll('projects');
  
  return projects.map(p => ({
    id: p.id,
    name: p.name,
    createdAt: p.createdAt,
    updatedAt: p.updatedAt
  }));
}

export async function saveFileToDB(
  projectId: string,
  fileId: string,
  name: string,
  content: Blob
): Promise<void> {
  const db = await getDB();
  await db.put('files', {
    id: fileId,
    projectId,
    name,
    content
  });
}

export async function loadFileFromDB(fileId: string): Promise<Blob | undefined> {
  const db = await getDB();
  const file = await db.get('files', fileId);
  return file?.content;
}
